/*
 * Pure Karl review logic for the Confusion panel. Everything here is free of
 * Office.js and React so the rules that protect a paid answer are testable:
 * which findings are eligible for a bulk request, which Sure verdicts a bulk
 * apply may resolve, how the "Only Likely and Uncertain" filter hides cases,
 * and how replacement offsets move after earlier edits.
 */
import { ApiRequestError } from "../api/client";
import { format } from "../i18n/translations";
import type { TranslationStrings } from "../i18n/translations";
import type {
  KarlAssists,
  KarlCaseStatus,
  KarlConfidenceLevel,
  KarlDecisionInput,
  KarlFinalDecision,
  KarlVerdict,
  KarlVerdictBatchItem,
  KarlVerdictBatchSummary,
  KarlVerdictInput,
  Occurrence,
  OccurrenceEdit,
} from "../state/types";

/* One finding is one stable key per scan; a new scan replaces every key. */
export const karlCaseKey = (index: number): string => String(index);

const confidenceLabels: Record<KarlConfidenceLevel, string> = {
  sure: "karlConfidenceSure",
  likely: "karlConfidenceLikely",
  uncertain: "karlConfidenceUncertain",
};

export const formatKarlVerdictBadge = (verdict: KarlVerdict, t: TranslationStrings): string =>
  format(verdict.decision === "keep" ? t.karlKeepBadge : t.karlChangeBadge, {
    word: verdict.word,
    confidence: t[confidenceLabels[verdict.confidence] as keyof TranslationStrings] as string,
  });

/* ─── Findings eligible for Karl ───────────────────────────────────────── */

/*
 * Karl answers about the sentence context, not the selection slice. Both
 * offsets must point at the finding word inside that sentence; a scan result
 * without them is shown but cannot be asked about.
 */
export const buildKarlFinding = (occurrence: Occurrence): KarlVerdictInput | null => {
  const { ruleId, word, sentenceContext, sentenceTargetStart, sentenceTargetEnd } = occurrence;
  if (typeof ruleId !== "string" || !ruleId) return null;
  if (typeof word !== "string" || !word) return null;
  if (typeof sentenceContext !== "string" || !sentenceContext) return null;
  if (!Number.isInteger(sentenceTargetStart) || !Number.isInteger(sentenceTargetEnd)) return null;
  if ((sentenceTargetStart as number) < 0) return null;
  if ((sentenceTargetEnd as number) <= (sentenceTargetStart as number)) return null;

  return {
    ruleId,
    word,
    sentence: sentenceContext,
    targetStart: sentenceTargetStart as number,
    targetEnd: sentenceTargetEnd as number,
  };
};

export interface KarlCandidate {
  index: number;
  finding: KarlVerdictInput;
}

export const buildKarlCandidates = (
  occurrences: Occurrence[],
  supportedRuleIds: ReadonlySet<string>
): KarlCandidate[] =>
  occurrences.flatMap((occurrence, index) => {
    if (!supportedRuleIds.has(occurrence.ruleId)) return [];
    const finding = buildKarlFinding(occurrence);
    return finding ? [{ index, finding }] : [];
  });

/* ─── Overview and filter ───────────────────────────────────────────────── */

export interface KarlOverviewRow {
  ruleId: string;
  title: string;
  total: number;
  answered: number;
  keep: number;
  change: number;
  sure: number;
  likely: number;
  uncertain: number;
}

/*
 * Counts come from the verdicts the account already has, so an overview row
 * always equals the badges it selects. Actions only show the totals and
 * Confidence counts the panel can explain in a narrow pane.
 */
export const buildKarlOverview = (
  occurrences: Occurrence[],
  verdicts: Record<string, KarlVerdict>,
  titleFor: (ruleId: string) => string
): KarlOverviewRow[] => {
  const rows = new Map<string, KarlOverviewRow>();

  occurrences.forEach((occurrence, index) => {
    let row = rows.get(occurrence.ruleId);
    if (!row) {
      row = {
        ruleId: occurrence.ruleId,
        title: titleFor(occurrence.ruleId),
        total: 0,
        answered: 0,
        keep: 0,
        change: 0,
        sure: 0,
        likely: 0,
        uncertain: 0,
      };
      rows.set(occurrence.ruleId, row);
    }

    row.total += 1;
    const verdict = verdicts[karlCaseKey(index)];
    if (verdict) {
      row.answered += 1;
      row[verdict.decision] += 1;
      row[verdict.confidence] += 1;
    }
  });

  return [...rows.values()];
};

/*
 * The filter hides Sure verdicts only. A finding Karl has not answered is not
 * Sure, so it stays in view until the reader decides or asks Karl.
 */
export const isKarlCaseHidden = (
  verdict: KarlVerdict | null | undefined,
  skipSure: boolean
): boolean => Boolean(skipSure && verdict?.confidence === "sure");

export const nextKarlVisibleIndex = (
  occurrences: Occurrence[],
  verdicts: Record<string, KarlVerdict>,
  skipSure: boolean,
  from: number
): number => {
  for (let index = Math.max(0, from); index < occurrences.length; index++) {
    if (!isKarlCaseHidden(verdicts[karlCaseKey(index)], skipSure)) return index;
  }
  return -1;
};

export const countKarlVisibleCases = (
  occurrences: Occurrence[],
  verdicts: Record<string, KarlVerdict>,
  skipSure: boolean
): number => {
  let count = 0;
  occurrences.forEach((_occurrence, index) => {
    if (!isKarlCaseHidden(verdicts[karlCaseKey(index)], skipSure)) count += 1;
  });
  return count;
};

/* ─── Bulk apply of Sure verdicts ───────────────────────────────────────── */

export interface KarlSureApplyEntry {
  index: number;
  ruleId: string;
  /* True means the replacement is accepted, false means the word is kept. */
  accepted: boolean;
}

export interface KarlSureApplyPlan {
  entries: KarlSureApplyEntry[];
  findings: number;
  /* How many of the entries change the selection; the confirmation names them. */
  changes: number;
}

/*
 * Only Sure verdicts enter a bulk apply. A finding the reader decided by hand
 * is left alone, and a finding that already carries the Accepted with Karl mark
 * is not resolved twice.
 */
export const buildKarlSureApplyPlan = (
  occurrences: Occurrence[],
  verdicts: Record<string, KarlVerdict>,
  reviewed: Record<string, boolean>,
  accepted: Record<string, "keep" | "change">
): KarlSureApplyPlan => {
  const entries: KarlSureApplyEntry[] = [];

  occurrences.forEach((occurrence, index) => {
    const key = karlCaseKey(index);
    const verdict = verdicts[key];
    if (!verdict || verdict.confidence !== "sure") return;
    if (reviewed[key] || accepted[key]) return;

    entries.push({
      index,
      ruleId: occurrence.ruleId,
      accepted: verdict.decision === "change",
    });
  });

  return {
    entries,
    findings: entries.length,
    changes: entries.filter((entry) => entry.accepted).length,
  };
};

/* ─── Decision records ──────────────────────────────────────────────────── */

/*
 * Records are built from the verdict the API returned and the finding's two
 * words — never from the sentence. A missing verdict means Karl never answered,
 * so there is no Karl decision to record.
 */
export const buildKarlDecision = (
  occurrence: Occurrence,
  verdict: KarlVerdict | null | undefined,
  decision: KarlFinalDecision
): KarlDecisionInput | null => {
  if (!verdict) return null;
  const word = typeof occurrence.word === "string" ? occurrence.word : "";
  if (!word) return null;

  return {
    ruleId: occurrence.ruleId,
    word,
    verdict: verdict.decision,
    verdictWord: verdict.word,
    confidence: verdict.confidence,
    decision,
  };
};

/* ─── Errors and batch statuses ─────────────────────────────────────────── */

export type KarlErrorOutcome =
  | { kind: "consent"; accountRequired: boolean }
  | { kind: "exhausted"; reason: "today" | "cycle" | "unknown" }
  | { kind: "unsupported" }
  | { kind: "limited" }
  | { kind: "unavailable" };

const hasDetailField = (error: ApiRequestError, field: string): boolean =>
  error.details.some((detail) => detail.field === field);

/*
 * The API names the exhausted window in `error.details`, and a consent refusal
 * names the account when no signed-in account can carry the acceptance. The UI
 * uses the account case to ask for sign-in instead of reopening the notice.
 */
export const classifyKarlError = (error: unknown): KarlErrorOutcome => {
  if (!(error instanceof ApiRequestError)) {
    return { kind: "unavailable" };
  }

  switch (error.code) {
    case "KARL_CONSENT_REQUIRED":
      return { kind: "consent", accountRequired: hasDetailField(error, "account") };
    case "KARL_ASSISTS_EXHAUSTED":
      return {
        kind: "exhausted",
        reason: hasDetailField(error, "assists.today")
          ? "today"
          : hasDetailField(error, "assists.cycle")
            ? "cycle"
            : "unknown",
      };
    case "KARL_RULE_UNSUPPORTED":
      return { kind: "unsupported" };
    default:
      return { kind: "unavailable" };
  }
};

export const caseStatusFromBatchResult = (result: KarlVerdictBatchItem): KarlCaseStatus => {
  if (result.status === "unanswered") {
    if (result.reason === "assist-limit") return { status: "limited" };
    return {
      status: "exhausted",
      reason:
        result.reason === "quota.today"
          ? "today"
          : result.reason === "quota.cycle"
            ? "cycle"
            : "unknown",
    };
  }
  if (result.status === "unsupported") return { status: "unsupported" };
  return { status: "unavailable" };
};

/* ─── Assists and estimates copy ────────────────────────────────────────── */

export const formatKarlAssists = (
  assists: KarlAssists,
  t: TranslationStrings,
  formatNumber: (value: number) => string
): string => {
  if (assists.unlimited) {
    return t.karlAssistsUnlimited;
  }
  if (assists.today.remaining === null) {
    return format(t.karlAssistsMonthOnly, {
      used: formatNumber(assists.cycle.used),
      month: formatNumber(assists.cycle.remaining ?? 0),
    });
  }
  return format(t.karlAssistsUsed, {
    used: formatNumber(assists.today.used),
    today: formatNumber(assists.today.remaining),
    month: formatNumber(assists.cycle.remaining ?? 0),
  });
};

export const formatKarlEstimate = (
  estimate: {
    chargeable: number;
    canAnswerAll: boolean;
    maximum: number | null;
    assists: KarlAssists | null;
  },
  t: TranslationStrings,
  formatNumber: (value: number) => string
): string => {
  if (!estimate.assists) {
    return format(t.karlBulkEstimateOnly, { count: formatNumber(estimate.chargeable) });
  }
  if (estimate.assists.unlimited) {
    return format(t.karlBulkEstimateUnlimited, { count: formatNumber(estimate.chargeable) });
  }
  if (estimate.assists.today.remaining === null) {
    return format(t.karlBulkEstimateMonthOnly, {
      count: formatNumber(estimate.chargeable),
      month: formatNumber(estimate.assists.cycle.remaining ?? 0),
    });
  }
  return format(t.karlBulkEstimate, {
    count: formatNumber(estimate.chargeable),
    today: formatNumber(estimate.assists.today.remaining),
    month: formatNumber(estimate.assists.cycle.remaining ?? 0),
  });
};

export const formatKarlBatchSummary = (
  summary: KarlVerdictBatchSummary,
  t: TranslationStrings,
  formatNumber: (value: number) => string
): string => {
  const parts: string[] = [];
  if (summary.answered > 0) {
    parts.push(format(t.karlBulkAnswered, { count: formatNumber(summary.answered) }));
  }
  if (summary.cached > 0) {
    parts.push(format(t.karlBulkCached, { count: formatNumber(summary.cached) }));
  }
  if (summary.unanswered > 0) {
    parts.push(format(t.karlBulkUnanswered, { count: formatNumber(summary.unanswered) }));
  }
  if (summary.failed > 0) {
    parts.push(format(t.karlBulkFailed, { count: formatNumber(summary.failed) }));
  }
  return parts.join(" · ");
};

/* ─── Replacement offsets inside the selection ──────────────────────────── */

export class OccurrenceChangedError extends Error {
  constructor() {
    super("OCCURRENCE_CHANGED");
    this.name = "OccurrenceChangedError";
  }
}

/*
 * Every applied replacement shifts the occurrences after it. The shift is kept
 * per occurrence instead of as one running total so an undo in the middle of
 * the review cannot move a later start by the wrong amount.
 */
export const resolveOccurrenceStart = (
  occurrences: Occurrence[],
  edits: Record<string, OccurrenceEdit>,
  index: number
): number => {
  const base = occurrences[index]?.start ?? 0;
  let shift = 0;

  for (const [key, edit] of Object.entries(edits)) {
    const other = Number(key);
    if (other === index) continue;
    if ((occurrences[other]?.start ?? 0) < base) shift += edit.delta;
  }

  return base + shift;
};

/* The 0-based match index Office's `search` needs for the target range. */
export const countOccurrencesBefore = (text: string, word: string, position: number): number => {
  if (!word) return 0;
  let count = 0;
  let found = text.indexOf(word);
  while (found !== -1 && found < position) {
    count += 1;
    found = text.indexOf(word, found + 1);
  }
  return count;
};

export interface OccurrenceTextEdit {
  workingText: string;
  edits: Record<string, OccurrenceEdit>;
  occurrenceIndex: number;
  currentWord: string;
}

const assertWordAt = (text: string, start: number, word: string): void => {
  if (!word || text.slice(start, start + word.length) !== word) {
    throw new OccurrenceChangedError();
  }
};

export const replaceOccurrenceText = ({
  workingText,
  occurrences,
  edits,
  index,
  replacement,
}: {
  workingText: string;
  occurrences: Occurrence[];
  edits: Record<string, OccurrenceEdit>;
  index: number;
  replacement: string;
}): OccurrenceTextEdit => {
  const occurrence = occurrences[index];
  const originalWord = typeof occurrence?.word === "string" ? occurrence.word : "";
  if (!originalWord) throw new OccurrenceChangedError();

  const key = karlCaseKey(index);
  const currentWord = edits[key]?.appliedWord ?? originalWord;
  const start = resolveOccurrenceStart(occurrences, edits, index);
  assertWordAt(workingText, start, currentWord);

  return {
    workingText:
      workingText.slice(0, start) + replacement + workingText.slice(start + currentWord.length),
    edits: {
      ...edits,
      [key]: { appliedWord: replacement, delta: replacement.length - currentWord.length },
    },
    occurrenceIndex: countOccurrencesBefore(workingText, currentWord, start),
    currentWord,
  };
};

export const undoOccurrenceText = ({
  workingText,
  occurrences,
  edits,
  index,
}: {
  workingText: string;
  occurrences: Occurrence[];
  edits: Record<string, OccurrenceEdit>;
  index: number;
}): OccurrenceTextEdit => {
  const occurrence = occurrences[index];
  const originalWord = typeof occurrence?.word === "string" ? occurrence.word : "";
  const key = karlCaseKey(index);
  const edit = edits[key];
  if (!originalWord || !edit) throw new OccurrenceChangedError();

  const start = resolveOccurrenceStart(occurrences, edits, index);
  assertWordAt(workingText, start, edit.appliedWord);

  const nextEdits = { ...edits };
  delete nextEdits[key];

  return {
    workingText:
      workingText.slice(0, start) +
      originalWord +
      workingText.slice(start + edit.appliedWord.length),
    edits: nextEdits,
    occurrenceIndex: countOccurrencesBefore(workingText, edit.appliedWord, start),
    currentWord: edit.appliedWord,
  };
};
